import { useEffect, useState } from "react";
import { FiArrowLeft, FiHelpCircle, FiUsers } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHeader from "../../../components/common/pageheader/PageHeader";

import {
  createSubject,
  deleteSubject,
  getSubjectById,
  getSubjects,
  updateSubject,
} from "../../../services/subjectsApi";

import ActionsMenu from "../../../components/common/actionsMenu/ActionsMenu";
import ConfirmDeleteModal from "../../../components/common/deleteModal/ConfirmDeleteModal";
import SubjectStudentsModal from "../SubjectStudentsModal/SubjectStudentsModal";
import TopicFormModal from "../topicPage/TopicFormModal";
import TableEmptyState from "../../../components/common/tableEmptyState/TableEmptyState";
import styles from "./SubjectDetailPage.module.css";
import Loader from "../../../components/common/Loader/Loader";
import { toast } from "react-toastify";

const SubjectDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const subjectId = Number(id);

  const [subject, setSubject] = useState(null);
  const [topics, setTopics] = useState([]);

  const [loadingSubject, setLoadingSubject] = useState(false);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [formValues, setFormValues] = useState({
    id: null,
    name: "",
    description: "",
  });
  const [saving, setSaving] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [topicToDelete, setTopicToDelete] = useState(null);

  const [isStudentsModalOpen, setIsStudentsModalOpen] = useState(false);
  const [topicForAssign, setTopicForAssign] = useState(null);

  const fetchSubject = async () => {
    try {
      setLoadingSubject(true);
      setError("");
      const raw = await getSubjectById(subjectId);
      const data = raw?.data || raw;
      setSubject(data);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        t("pages.subjectDetail.loadError");
      setError(msg);
    } finally {
      setLoadingSubject(false);
    }
  };

  const fetchTopics = async (searchTerm) => {
    try {
      setLoadingTopics(true);
      setError("");

      const filters = {
        parentId: subjectId,
        pageNo: 1,
        pageSize: 100,
      };

      if (searchTerm && searchTerm.trim().length > 0) {
        filters.name = searchTerm.trim();
      }

      const data = await getSubjects(filters);
      const list = Array.isArray(data?.content) ? data.content : [];
      setTopics(list);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        t("pages.subjectDetail.topicsLoadError");
      setError(msg);
    } finally {
      setLoadingTopics(false);
    }
  };

  useEffect(() => {
    if (!Number.isNaN(subjectId)) {
      fetchSubject();
      fetchTopics();
    }
  }, [subjectId]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchTopics(search);
  };

  const openCreateModal = () => {
    setFormMode("create");
    setFormValues({ id: null, name: "", description: "" });
    setIsModalOpen(true);
  };

  const openEditModal = (topic) => {
    setFormMode("edit");
    setFormValues({
      id: topic.id,
      name: topic.name || "",
      description: topic.description || "",
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormValues({ id: null, name: "", description: "" });
    setError("");
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      if (!formValues.name.trim()) {
        setError(t("pages.subjectDetail.topicNameRequired"));
        setSaving(false);
        return;
      }

      if (formMode === "create") {
        await createSubject({
          name: formValues.name.trim(),
          description: formValues.description?.trim() || "",
          parentId: subjectId,
        });

        toast.success(t("pages.subjectDetail.topicCreated"));
      } else {
        await updateSubject({
          id: formValues.id,
          name: formValues.name.trim(),
          description: formValues.description?.trim() || "",
          parentId: subjectId,
        });

        toast.success(t("pages.subjectDetail.topicUpdated"));
      }

      closeModal();
      fetchTopics(search);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        t("pages.subjectDetail.operationFailed");
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const openDeleteTopicModal = (topic) => {
    setTopicToDelete(topic);
    setDeleteModalOpen(true);
  };

  const confirmDeleteTopic = async () => {
    if (!topicToDelete) return;

    try {
      setLoadingTopics(true);
      setError("");

      await deleteSubject(topicToDelete.id);
      toast.success(t("pages.subjectDetail.topicDeleted"));

      setDeleteModalOpen(false);
      setTopicToDelete(null);
      fetchTopics(search);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        t("pages.subjectDetail.deleteFailed");
      setError(msg);
    } finally {
      setLoadingTopics(false);
    }
  };

  const cancelDeleteTopic = () => {
    setDeleteModalOpen(false);
    setTopicToDelete(null);
  };

  const goBack = () => {
    navigate("/subjects/base");
  };

  const openStudentsModal = (topic) => {
    setTopicForAssign(topic);
    setIsStudentsModalOpen(true);
  };

  const closeStudentsModal = () => {
    setIsStudentsModalOpen(false);
    setTopicForAssign(null);
  };

  const handleAssignedStudents = async () => {
    await fetchTopics(search);
  };

  return (
    <div className={styles.container}>
      <PageHeader
        title={
          subject
            ? t("pages.subjectDetail.title", { name: subject.subjectBase?.name || subject.name })
            : t("pages.subjectDetail.fallbackTitle")
        }
      />

      <div className={styles.topBar}>
        <button type="button" onClick={goBack} className={styles.backBtn}>
          <FiArrowLeft size={16} />
          <span>{t("common.back")}</span>
        </button>

        <form onSubmit={handleSearchSubmit} className={styles.searchForm}>
          <input
            type="text"
            placeholder={t("pages.subjectDetail.searchByTopicName")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
          <button type="submit" className={styles.primaryBtn}>
            {t("common.search")}
          </button>
        </form>

        <button
          type="button"
          onClick={openCreateModal}
          className={styles.secondaryBtn}
        >
          {t("pages.subjectDetail.newTopic")}
        </button>
      </div>

      {loadingSubject && <Loader fullscreen />}
      {error && <p className={styles.error}>{error}</p>}

      {!loadingTopics && topics.length === 0 && !error && (
        <TableEmptyState
          title={t("pages.subjectDetail.noTopicsTitle")}
          description={t("pages.subjectDetail.noTopicsDescription")}
        />
      )}

      {!loadingTopics && topics.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t("pages.subjectDetail.topicName")}</th>
              <th>{t("pages.subjectDetail.description")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {topics.map((topic) => (
              <tr key={topic.id}>
                <td>{topic.name}</td>
                <td>{topic.description}</td>
                <td className={styles.actionsCell}>
                  <div className={styles.actions}>
                    <button
                      type="button"
                      onClick={() => navigate(`/question-bank?subjectId=${topic.id}`)}
                      className={styles.actionBtn}
                      title={t("pages.subjectDetail.manageQuestions")}
                    >
                      <FiHelpCircle size={16} />
                      <span>{t("pages.subjectDetail.questionsAction")}</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => openStudentsModal(topic)}
                      className={styles.actionBtn}
                      title={t("pages.subjectDetail.assignStudent")}
                    >
                      <FiUsers size={16} />
                      <span>{t("pages.subjectDetail.studentsAction")}</span>
                    </button>

                    <ActionsMenu
                      onEdit={() => openEditModal(topic)}
                      onDelete={() => openDeleteTopicModal(topic)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <TopicFormModal
        isOpen={isModalOpen}
        mode={formMode}
        values={formValues}
        onChange={handleFormChange}
        onClose={closeModal}
        onSubmit={handleFormSubmit}
        saving={saving}
        error={error}
        subjectName={subject?.name}
      />

      <ConfirmDeleteModal
        isOpen={deleteModalOpen}
        onConfirm={confirmDeleteTopic}
        onCancel={cancelDeleteTopic}
        message={
          topicToDelete
            ? t("pages.subjectDetail.deleteConfirm", { name: topicToDelete.name })
            : ""
        }
      />

      <SubjectStudentsModal
        isOpen={isStudentsModalOpen}
        subject={topicForAssign}
        onClose={closeStudentsModal}
        onAssigned={handleAssignedStudents}
      />
    </div>
  );
};

export default SubjectDetailPage;
